import { formatMoney, formatRate, type CurrencyFormat } from "../../domain/money/format";
import type { ReceiptDocument } from "./compose";
import { Slot } from "../../ui/slots/Slot";

/**
 * The receipt, rendered for a screen and for print from one markup.
 *
 * `print.css` restyles this to 80 mm at print time rather than maintaining a
 * second component — two renderers would be two places for a receipt to go
 * wrong, and only one of them gets looked at.
 */

interface ScreenReceiptProps {
  document: ReceiptDocument;
  currency: CurrencyFormat;
}

export function ScreenReceipt({ document: doc, currency }: ScreenReceiptProps) {
  return (
    <article className="receipt">
      <header className="receipt__head">
        <h2 className="receipt__business">{doc.business.header ?? doc.business.name}</h2>
        {doc.business.address && <p className="receipt__line-sm">{doc.business.address}</p>}
        {doc.business.taxIdentifier && (
          <p className="receipt__line-sm">TIN {doc.business.taxIdentifier}</p>
        )}
      </header>

      <dl className="receipt__meta">
        <div>
          <dt>No.</dt>
          <dd className="money">{doc.documentNumber}</dd>
        </div>
        <div>
          <dt>Date</dt>
          <dd className="money">{new Date(doc.issuedAt).toLocaleString(currency.locale)}</dd>
        </div>
        <div>
          <dt>Cashier</dt>
          <dd>{doc.cashier}</dd>
        </div>
        <div>
          <dt>Type</dt>
          <dd>{doc.orderType}</dd>
        </div>
      </dl>

      {doc.reprint && <p className="receipt__reprint">— REPRINT —</p>}

      <ul className="receipt__lines">
        {doc.lines.map((line) => (
          <li key={line.lineNumber} className="receipt__item">
            <div className="receipt__item-row">
              <span className="receipt__item-qty money">{line.quantity}×</span>
              <span className="receipt__item-name">
                {line.name}
                {line.variant && ` (${line.variant})`}
              </span>
              <span className="receipt__item-total money">
                {formatMoney(line.lineTotal, currency)}
              </span>
            </div>
            {line.modifiers.length > 0 && (
              <p className="receipt__item-mods">
                {line.modifiers.map((m) => m.name).join(", ")}
              </p>
            )}
            {line.note && <p className="receipt__item-note">{line.note}</p>}
            <Slot id="receipt.line" lineNumber={line.lineNumber} />
          </li>
        ))}
      </ul>

      <div className="receipt__totals">
        <div className="receipt__row">
          <span>Subtotal</span>
          <span className="money">{formatMoney(doc.totals.subtotal, currency)}</span>
        </div>

        {doc.discounts.map((discount, i) => (
          <div key={i} className="receipt__row">
            <span>
              {discount.label}
              {discount.cardholderName && (
                <span className="receipt__cardholder">
                  {" "}
                  {discount.cardholderName}
                  {discount.cardholderId && ` · ${discount.cardholderId}`}
                </span>
              )}
            </span>
            <span className="money">−{formatMoney(discount.amount, currency)}</span>
          </div>
        ))}

        {/* VAT breakdown appears only under a VAT regime, and only because the
            SALE says so — not because the business is VAT-registered today. */}
        {doc.tax && (
          <>
            <div className="receipt__row receipt__row--muted">
              <span>VATable sale</span>
              <span className="money">{formatMoney(doc.tax.taxable, currency)}</span>
            </div>
            {doc.tax.exempt > 0 && (
              <div className="receipt__row receipt__row--muted">
                <span>VAT-exempt sale</span>
                <span className="money">{formatMoney(doc.tax.exempt, currency)}</span>
              </div>
            )}
            <div className="receipt__row receipt__row--muted">
              <span>VAT ({formatRate(doc.tax.rateBp, currency.locale)})</span>
              <span className="money">{formatMoney(doc.tax.amount, currency)}</span>
            </div>
          </>
        )}

        <div className="receipt__row receipt__row--total">
          <span>Total</span>
          <span className="money">{formatMoney(doc.totals.total, currency)}</span>
        </div>

        {doc.tenders.map((tender, i) => (
          <div key={i} className="receipt__row">
            <span>
              {tender.type}
              {tender.reference && ` · ${tender.reference}`}
            </span>
            <span className="money">{formatMoney(tender.amount, currency)}</span>
          </div>
        ))}

        {doc.changeGiven > 0 && (
          <div className="receipt__row receipt__row--total">
            <span>Change</span>
            <span className="money">{formatMoney(doc.changeGiven, currency)}</span>
          </div>
        )}
      </div>

      <footer className="receipt__foot">
        <Slot id="receipt.footer" />
        {doc.business.footer && <p className="receipt__tagline">{doc.business.footer}</p>}
        {/* Not removable through configuration (FR-034). */}
        <p className="receipt__notice">{doc.notice}</p>
      </footer>
    </article>
  );
}

export default ScreenReceipt;
